import { anyoneListening, inputMessages, LIVE_FLUSH_MS, type LiveDown, type LiveDrive, type LiveRoster, type LiveUp } from '../net/live';
import { encodeInputs } from '../sim/input';

/** Kept open through quiet spells (some proxies drop a silent connection): a ping this often, answered by the room unwoken. */
const PING_MS = 25_000;
/** The room closes a seat with this when the same player opens the game somewhere else. */
const REPLACED = 4000;
/** Lost, it tries again after this long, longer each time (to this at most). */
const RETRY_MS = 2_000;
const RETRY_MAX_MS = 30_000;

/** What the game is doing, asked four times a second: driving (its run's inputs so far) or not. */
export interface LiveSource {
  drive: LiveDrive | null;
  /** The run's inputs since the grid (Session.packed). */
  packed: readonly number[];
}

/**
 * The game's line to its Discord server's room (worker/live.ts), while it's open and on screen. As a driver: says
 * when it's driving, and while anyone else in the room watches or drives, sends its inputs four times a second (all
 * of its run when someone new starts watching or driving). Watching or driving, it hands on the others' inputs.
 */
export class LiveLink {
  roster: LiveRoster | null = null;
  onRoster: (r: LiveRoster) => void = () => {};
  onInputs: (key: string, run: number, from: number, encoded: string) => void = () => {};
  private ws: WebSocket | null = null;
  private timer = 0;
  private pinger = 0;
  private retry = 0;
  private retryTimer = 0;
  /** The last drive said (as text), and how much of which run has gone out. */
  private said = 'null';
  private sentRun = -1;
  private sent = 0;
  private focus: string | null = null;

  constructor(
    private url: () => string,
    private source: () => LiveSource,
  ) {}

  start(): void {
    this.open();
    // A hidden game (another app, the phone locked) leaves the room; it comes back when it's on screen again.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.close();
      else if (!this.ws) this.open();
    });
    window.addEventListener('focus', () => !this.ws && this.open());
  }

  /** Watch a driver (their key), or stop watching (null). */
  watch(focus: string | null): void {
    if (focus === this.focus) return;
    this.focus = focus;
    // Watching isn't driving: the room hears that from the watch itself.
    if (focus) this.said = 'null';
    this.send({ t: 'watch', focus });
  }

  /** Some inputs went missing here: the drivers send their runs again. */
  need(): void {
    this.send({ t: 'need' });
  }

  /** The switch: whether the server's channel shows this player while they drive (a new seat hears it from the address). */
  announce(on: boolean): void {
    this.send({ t: 'announce', on });
  }

  private open(): void {
    window.clearTimeout(this.retryTimer);
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      this.again();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      // A fresh seat: say everything again.
      this.said = 'null';
      this.sent = 0;
      if (this.focus) this.send({ t: 'watch', focus: this.focus });
      this.tick();
    };
    ws.onmessage = (e) => this.heard(e.data);
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopTimers();
      this.roster = null;
      // Opened on another device (or tab): that one has the seat now. This one comes back when it's on screen
      // again, rather than the two taking it from each other over and over.
      if (e.code === REPLACED) return;
      if (document.visibilityState !== 'hidden') this.again();
    };
    this.timer = window.setInterval(() => this.tick(), LIVE_FLUSH_MS);
    this.pinger = window.setInterval(() => this.ws?.readyState === WebSocket.OPEN && this.ws.send('ping'), PING_MS);
  }

  private close(): void {
    const ws = this.ws;
    this.ws = null;
    this.stopTimers();
    window.clearTimeout(this.retryTimer);
    this.roster = null;
    ws?.close(1000, 'hidden');
  }

  private stopTimers(): void {
    window.clearInterval(this.timer);
    window.clearInterval(this.pinger);
  }

  private again(): void {
    const wait = Math.min(RETRY_MAX_MS, RETRY_MS * 2 ** this.retry++);
    this.retryTimer = window.setTimeout(() => this.open(), wait);
  }

  private send(m: LiveUp): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(m));
  }

  private heard(data: unknown): void {
    if (typeof data !== 'string' || data === 'pong') return;
    let m: LiveDown;
    try {
      m = JSON.parse(data) as LiveDown;
    } catch {
      return;
    }
    if (m.t === 'roster') {
      const { t: _, ...r } = m;
      this.roster = r;
      this.onRoster(r);
    } else if (m.t === 'in') this.onInputs(m.key, m.run, m.from, m.d);
    // Someone started watching (or missed some): all of this run again, from the grid.
    else if (m.t === 'need') this.sent = 0;
  }

  /** Four times a second: what the game is doing, and (while anyone watches) what it has driven since. */
  private tick(): void {
    if (this.ws?.readyState !== WebSocket.OPEN || this.focus) return;
    const { drive, packed } = this.source();
    const said = JSON.stringify(drive);
    if (said !== this.said) {
      this.said = said;
      this.send({ t: 'drive', drive });
    }
    if (!drive) return;
    if (drive.run !== this.sentRun) {
      this.sentRun = drive.run;
      this.sent = 0;
    }
    if (!this.roster || !anyoneListening(this.roster) || this.sent >= packed.length) return;
    for (const m of inputMessages(drive.run, packed, this.sent, encodeInputs)) this.send(m);
    this.sent = packed.length;
  }
}
